import type { VoterSummary, VoterMapPoint } from './stores/voters';
import {
  householdOutreachState,
  outreachColor,
  OUTREACH_DARK_INK_FILLS,
  type TextOutreach,
} from './outreach';

/** CSS color for a voter pin based on latest contact state. */
export function pinColor(v: VoterSummary): string {
  if (v.do_not_contact) return '#4B5563';                    // DNC — slate gray
  if (v.latest_contact_result !== 'spoke') {
    // Outcome tier: a real donation / volunteer sign-up outranks every
    // non-spoke touch, but a spoke's support color stays authoritative.
    if (v.has_donated || v.has_volunteered) return '#8e6bb8'; // supporter action — violet
    if (!v.latest_contact_result) return '#888888';           // never contacted — gray
    if (v.latest_contact_result === 'not_home' || v.latest_contact_result === 'no_response')
      return '#c2a878';                                       // attempted, no answer — tan
    return '#6baed6';                                         // signed/other touch — blue
  }
  switch (v.latest_support_level) {
    case 1: return '#a50026'; // strong against — dark red
    case 2: return '#f46d43'; // lean against — orange
    case 3: return '#f59e0b'; // undecided — amber (darker than the old #fee08b for contrast)
    case 4: return '#74c476'; // lean support — light green
    case 5: return '#006d2c'; // strong support — dark green
    default: return '#888888';
  }
}

/**
 * Fade a pin to mark a voter who has already voted: there is nothing left to
 * knock for, so the pin recedes instead of competing with the live ones. The
 * attribute goes on the root <svg>, so every pin geometry (including the DNC
 * mark) is faded the same way and nothing else about it changes.
 */
export function mutedPinSvg(svg: string): string {
  return svg.replace('<svg ', '<svg opacity="0.4" ');
}

/** Average support level of an array of voters (null if all unknown). */
export function avgSupportLevel(voters: VoterSummary[]): number | null {
  const levels = voters
    .map(v => v.latest_support_level)
    .filter((l): l is number => l !== null);
  if (levels.length === 0) return null;
  return levels.reduce((a, b) => a + b, 0) / levels.length;
}

/** CSS color for a cluster based on avg support level of its members. */
export function clusterColor(voters: VoterSummary[]): { bg: string; text: string } {
  const avg = avgSupportLevel(voters);
  if (avg === null) return { bg: '#888888', text: '#fff' };
  if (avg < 2)      return { bg: '#a50026', text: '#fff' };
  if (avg < 2.5)    return { bg: '#f46d43', text: '#fff' };
  if (avg < 3.5)    return { bg: '#f59e0b', text: '#333' };
  if (avg < 4.5)    return { bg: '#74c476', text: '#333' };
  return               { bg: '#006d2c', text: '#fff' };
}

/**
 * Cluster fill for outreach mode: one neutral slate, painted regardless of
 * the children's states. Averaging outreach states the way clusterColor
 * averages support would saturate: household precedence lets a single
 * opted_in voter paint a cluster of hundreds green even when the cluster's
 * most common state is not_contacted, and at real data volume (25k voters,
 * 7 percent opted in) every district-zoom cluster read "Opted in" under a
 * legend that says otherwise. Distinct from every outreach legend swatch and
 * every clusterColor output so it can never be mistaken for either.
 */
export const OUTREACH_CLUSTER_FILL = '#374151';

/**
 * Picks the bg/text a map cluster renders with for the active view mode.
 * Outreach mode gets the neutral OUTREACH_CLUSTER_FILL (see above); every
 * other mode keeps clusterColor's support-average palette unchanged. Pure so
 * the mode-to-fill decision is testable without Leaflet.
 */
export function clusterIconColor(
  mode: 'support' | 'coverage' | 'uncontacted' | 'outreach',
  voters: VoterSummary[],
): { bg: string; text: string } {
  if (mode === 'outreach') return { bg: OUTREACH_CLUSTER_FILL, text: '#ffffff' };
  return clusterColor(voters);
}

/**
 * SVG circle pin — encodes contact state via both color AND size/stroke
 * so color-blind volunteers get a secondary signal.
 * never-contacted: small (10), no ring
 * contacted/not-spoken: medium (12), thin ring
 * spoke: large (14), thick ring
 */
export function pinSvg(v: VoterSummary): string {
  // DNC voters get their own distinctive "no entry" mark: gray-filled
  // circle with a red diagonal slash. Still clickable, still on the map
  // so volunteers know not to knock — but visually unmistakable from
  // a regular voter dot.
  if (v.do_not_contact) {
    const r = 6;
    const strokeW = 1.5;
    const cx = r + strokeW + 1;
    const cy = cx;
    const size = (r + strokeW) * 2 + 2;
    const off = r * 0.72;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="#4B5563" stroke="white" stroke-width="${strokeW}"/>
      <line x1="${cx - off}" y1="${cy + off}" x2="${cx + off}" y2="${cy - off}"
            stroke="#D62E2E" stroke-width="2" stroke-linecap="round"/>
    </svg>`;
  }
  const color = pinColor(v);
  const hasContact = !!v.latest_contact_result;
  const spoke = v.latest_contact_result === 'spoke';
  // Never-contacted pins are now larger (6.5/1.5) so they don't disappear
  // against busy tiles. Contacted/spoke keep their size hierarchy.
  const r = spoke ? 7 : hasContact ? 6 : 6.5;
  const strokeW = spoke ? 2.5 : 1.5;
  return _dotWithOutline(color, r, strokeW);
}

/**
 * Render a filled dot with BOTH a white inner ring (the existing stroke) and a
 * dark outer ring, so the pin reads against light AND dark map tiles. The dark
 * ring sits just outside the white stroke at 1px.
 *
 * `hollow` swaps the fill/stroke to the "no data" style used by outreach mode:
 * a white-filled dot ringed in `color` instead of a `color`-filled dot ringed
 * in white. The dark outline ring is unaffected either way, so every pin mode
 * shares one geometry.
 */
function _dotWithOutline(color: string, r: number, strokeW: number, hollow = false): string {
  const darkW = 1;
  // Total visual radius includes the white stroke (centered on r) plus the
  // dark ring drawn at r + strokeW/2.
  const outerR = r + strokeW / 2 + darkW / 2;
  const pad = 1;
  const c = outerR + pad;
  const size = c * 2;
  const fill = hollow ? '#ffffff' : color;
  const stroke = hollow ? color : 'white';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
    <circle cx="${c}" cy="${c}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeW}"/>
    <circle cx="${c}" cy="${c}" r="${r + strokeW / 2}" fill="none" stroke="#1f2937" stroke-width="${darkW}"/>
  </svg>`;
}

/** Days between an ISO timestamp and `nowMs`; Infinity for null. */
export function daysSince(iso: string | null, nowMs: number): number {
  if (!iso) return Infinity;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return Infinity;
  return Math.max(0, (nowMs - then) / 86400000);
}

/** Coverage-mode color: green when fresh, red when stale or never. */
export function coverageColor(iso: string | null, nowMs: number): string {
  const d = daysSince(iso, nowMs);
  if (d < 7)   return '#006d2c'; // dark green — fresh
  if (d < 30)  return '#74c476'; // light green
  if (d < 60)  return '#f59e0b'; // amber (darker than the old #fee08b for contrast)
  if (d < 120) return '#f46d43'; // orange
  return '#a50026';              // red — stale / never
}

/** SVG pin sized like pinSvg() but colored by coverage instead of support. */
export function pinSvgCoverage(v: VoterMapPoint, nowMs: number): string {
  if (v.do_not_contact) {
    // DNC trumps coverage — reuse the existing DNC mark for visual consistency.
    return pinSvg(v as unknown as VoterSummary);
  }
  const color = coverageColor(v.latest_contact_at, nowMs);
  return _dotWithOutline(color, 6, 1.5);
}

/** SVG pin coloured by consultant text-outreach state instead of support. */
export function pinSvgOutreach(v: VoterMapPoint): string {
  if (v.do_not_contact) {
    // DNC trumps outreach, exactly as it trumps support and coverage. Reuse
    // the existing DNC mark so the glyph means one thing in every view.
    return pinSvg(v as unknown as VoterSummary);
  }
  const state = v.text_outreach ?? 'none';
  if (state === 'none') return _dotWithOutline(outreachColor('none'), 6, 1.5, true);
  return _dotWithOutline(outreachColor(state), 6, 1.5);
}

// ── Household markers ────────────────────────────────────────────────────────
//
// When 2+ voters share an exact lat/lng (same building), they render as a
// single "household" marker instead of stacking. Color is the average of
// known support levels (same bands as clusterColor); a small count badge in
// the center signals "click to see who lives here."

/** Support-mode color for a household of voters at one address. */
export function householdColor(voters: VoterMapPoint[]): string {
  if (voters.every(v => v.do_not_contact)) return '#4B5563';
  const living = voters.filter(v => !v.do_not_contact);
  const levels = living
    .map(v => v.latest_support_level)
    .filter((l): l is number => l !== null);
  if (levels.length === 0) {
    const anyContacted = living.some(v => !!v.latest_contact_result);
    return anyContacted ? '#6baed6' : '#888888';
  }
  const avg = levels.reduce((a, b) => a + b, 0) / levels.length;
  if (avg < 2)   return '#a50026';
  if (avg < 2.5) return '#f46d43';
  if (avg < 3.5) return '#f59e0b';
  if (avg < 4.5) return '#74c476';
  return            '#006d2c';
}

function _textOn(color: string): string {
  // Dark numerals on the light fills for legibility; white on darker fills.
  // #f59e0b and #74c476 are the light end of the Support/Coverage palettes;
  // OUTREACH_DARK_INK_FILLS (from outreach.ts) is the light end of the
  // outreach palette, so a recolour there cannot silently desync this list.
  return (
    color === '#f59e0b' ||
    color === '#74c476' ||
    OUTREACH_DARK_INK_FILLS.includes(color)
  )
    ? '#1a1a1a'
    : '#ffffff';
}

/**
 * `hollow` swaps to the "no data" style (white fill, coloured ring, dark
 * numerals) shared with `_dotWithOutline`, so the household badge's geometry
 * and dark outline ring exist in exactly one place alongside the pin's.
 */
function _householdCircle(color: string, count: number, hollow = false): string {
  const r = 9;
  const strokeW = 2;
  const darkW = 1;
  const outerR = r + strokeW / 2 + darkW / 2;
  const c = outerR + 1;
  const size = c * 2;
  const fill = hollow ? '#ffffff' : color;
  const stroke = hollow ? color : 'white';
  const textColor = hollow ? '#1a1a1a' : _textOn(color);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
    <circle cx="${c}" cy="${c}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeW}"/>
    <circle cx="${c}" cy="${c}" r="${r + strokeW / 2}" fill="none" stroke="#1f2937" stroke-width="${darkW}"/>
    <text x="${c}" y="${c}" text-anchor="middle" dominant-baseline="central" font-size="11" font-family="sans-serif" font-weight="700" fill="${textColor}">${count}</text>
  </svg>`;
}

function _householdDnc(): string {
  // All-DNC household: distinctive DNC mark, no count (the slash carries the
  // primary signal; count would crowd the glyph).
  const r = 9;
  const strokeW = 2;
  const size = (r + strokeW) * 2 + 2;
  const c = r + strokeW + 1;
  const off = r * 0.72;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
    <circle cx="${c}" cy="${c}" r="${r}" fill="#4B5563" stroke="white" stroke-width="${strokeW}"/>
    <line x1="${c - off}" y1="${c + off}" x2="${c + off}" y2="${c - off}"
          stroke="#D62E2E" stroke-width="2.5" stroke-linecap="round"/>
  </svg>`;
}

/** Support-mode SVG for a household marker. */
export function householdSvg(voters: VoterMapPoint[]): string {
  if (voters.every(v => v.do_not_contact)) return _householdDnc();
  return _householdCircle(householdColor(voters), voters.length);
}

/** Coverage-mode SVG: color by the most-recent contact across the household. */
export function householdSvgCoverage(voters: VoterMapPoint[], nowMs: number): string {
  if (voters.every(v => v.do_not_contact)) return _householdDnc();
  let latestMs = -Infinity;
  for (const v of voters) {
    if (v.do_not_contact || !v.latest_contact_at) continue;
    const t = Date.parse(v.latest_contact_at);
    if (Number.isFinite(t)) latestMs = Math.max(latestMs, t);
  }
  const iso = latestMs === -Infinity ? null : new Date(latestMs).toISOString();
  return _householdCircle(coverageColor(iso, nowMs), voters.length);
}

/**
 * Outreach state for a household of voters at one address.
 *
 * Do-not-contact members are excluded, the same way householdColor averages
 * only the contactable members: their consultant state must not colour a pin
 * a canvasser is being told to skip. An all-DNC household returns 'none' and
 * never reaches a colour, because householdSvgOutreach and the icon cache key
 * both branch on all-DNC first.
 */
export function householdOutreachStateOf(voters: VoterMapPoint[]): TextOutreach | 'none' {
  return householdOutreachState(
    voters.filter(v => !v.do_not_contact).map(v => v.text_outreach ?? null),
  );
}

/** Outreach-mode SVG for a household marker. */
export function householdSvgOutreach(voters: VoterMapPoint[]): string {
  if (voters.every(v => v.do_not_contact)) return _householdDnc();
  const state = householdOutreachStateOf(voters);
  if (state === 'none') return _householdCircle(outreachColor('none'), voters.length, true);
  return _householdCircle(outreachColor(state), voters.length);
}
